import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Warning, CaretDown, CaretUp, Copy } from "@phosphor-icons/react";

export default function DuplicateAlert({ duplicates }) {
  const [expanded, setExpanded] = useState(false);

  if (!duplicates || duplicates.length === 0) return null;

  // Group duplicates by original page
  const grouped = {};
  duplicates.forEach(d => {
    const key = d.page_a;
    if (!grouped[key]) {
      grouped[key] = {
        original: d.page_a,
        classification: d.classification,
        copies: []
      };
    }
    grouped[key].copies.push(d.page_b);
  });

  const groups = Object.values(grouped);
  const totalDuplicatePages = duplicates.length;

  return (
    <div
      className="flex-shrink-0 border-b border-red-200 bg-red-50"
      data-testid="duplicate-alert"
    >
      {/* Compact header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-red-100/50 transition-colors"
        data-testid="duplicate-alert-toggle"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 rounded-full bg-red-100 border border-red-300 flex items-center justify-center flex-shrink-0">
            <Copy size={13} weight="bold" className="text-red-600" />
          </div>
          <span className="text-sm font-semibold text-red-900">
            {totalDuplicatePages} Duplicate{totalDuplicatePages > 1 ? " Pages" : " Page"} Detected
          </span>
          <Badge variant="outline" className="text-[10px] bg-red-100 text-red-700 border-red-300 px-1.5 py-0">
            {groups.length} group{groups.length > 1 ? "s" : ""}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-red-500">
            {expanded ? "Collapse" : "View details"}
          </span>
          {expanded
            ? <CaretUp size={14} className="text-red-500" />
            : <CaretDown size={14} className="text-red-500" />
          }
        </div>
      </button>

      {/* Expanded detail — grouped table */}
      {expanded && (
        <div className="px-4 pb-3 pt-0">
          <div className="border border-red-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-red-50 border-b border-red-200">
                  <th className="px-3 py-1.5 text-left font-semibold text-red-800 uppercase tracking-wider">Original Page</th>
                  <th className="px-3 py-1.5 text-left font-semibold text-red-800 uppercase tracking-wider">Document Type</th>
                  <th className="px-3 py-1.5 text-left font-semibold text-red-800 uppercase tracking-wider">Duplicate Pages</th>
                  <th className="px-3 py-1.5 text-right font-semibold text-red-800 uppercase tracking-wider">Count</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g, idx) => (
                  <tr key={idx} className="border-b border-red-100 last:border-0 hover:bg-red-50/50">
                    <td className="px-3 py-2 font-medium text-gray-900">
                      Page {g.original}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {(g.classification || "unknown").replace(/_/g, " ")}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {g.copies.map((p, i) => (
                        <span key={p}>
                          <span className="font-medium text-red-700">P{p}</span>
                          {i < g.copies.length - 1 && <span className="text-gray-400">, </span>}
                        </span>
                      ))}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-700 font-bold text-[10px]">
                        {g.copies.length}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-red-400 mt-1.5">
            Duplicate pages are excluded from data extraction and amount calculation.
          </p>
        </div>
      )}
    </div>
  );
}
